/* ============================================================================
   ALT-COL-002 / ALT-CARD-003: Collection card image carousel (progressive
   enhancement).

   Desktop: it's ONE carousel of [1,2,3,4] that loops both ways (1-2-3-4-1…).
     · rest      → image 1 (hero)
     · hover     → image 2 APPEARS via crossfade (the track really moves to img 2,
                   so the next slide goes to img 3: not back to img 2)
     · arrows    → slide to the neighbour, seamless loop (clones, no rewind)
     · mouseout  → crossfade back to image 1
   The crossfade is a "snapshot" technique: cover the media with the outgoing
   image, jump the track underneath (masked), then fade the snapshot out.

   Mobile: native swipe, one crisp image at a time (scroll-snap-stop) + a flush
   full-bleed progress line. No dependency on Dawn slider JS.
   ========================================================================== */
(function () {
  'use strict';

  var mqDesktop = window.matchMedia('(min-width: 750px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var XFADE_MS = 340; // matches --alt-motion-base

  function Carousel(root) {
    this.root = root; // .alt-pcard__media
    this.track = root.querySelector('[data-pcard-track]');
    this.thumb = root.querySelector('[data-pcard-progress]');
    this.prevBtn = root.querySelector('[data-pcard-prev]');
    this.nextBtn = root.querySelector('[data-pcard-next]');
    this.xfade = root.querySelector('[data-pcard-xfade]'); // crossfade snapshot <img>
    if (!this.track) return;

    this.realCount = this.track.children.length; // slides before any clones
    this.cloned = false;
    this.domIndex = 0; // DOM index of the currently-shown real slide
    this.pendingSnap = null; // DOM index to silently snap to once a wrap settles
    this.snapTimer = 0;
    this.ticking = false;
    this.xfadeTimer = 0; // debounce for hover crossfades (prevents flash on quick enter/leave)

    this.bind();
    this.setupViewport();
    this.updateProgress();
  }

  // Distance from one slide's left edge to the next, in exact (fractional) px.
  // clientWidth rounds to an integer, but a card is often 258.25px or 313.5px
  // wide, so jumping to index * clientWidth landed up to half a pixel off and
  // let a sliver of the neighbouring photo (the last, usually dark, detail
  // shot) show at the card's edge, only at screen widths where the card width
  // is not a whole number. The CSS gap between slides (desktop) absorbs what
  // sub-pixel rounding is left, and is added here so the offsets stay exact.
  Carousel.prototype.slideW = function () {
    var w = this.track.getBoundingClientRect().width || this.track.clientWidth || 1;
    var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    return w + gap;
  };

  Carousel.prototype.scrollTrack = function (left, smooth) {
    var behavior = smooth && !mqReduce.matches ? 'smooth' : 'auto';
    try {
      this.track.scrollTo({ left: left, behavior: behavior });
    } catch (e) {
      this.track.scrollLeft = left;
    }
  };

  Carousel.prototype.jumpTo = function (domIndex) {
    this.scrollTrack(domIndex * this.slideW(), false);
  };
  Carousel.prototype.animateTo = function (domIndex) {
    this.scrollTrack(domIndex * this.slideW(), true);
  };

  /* ---- Seamless-loop clones (desktop only) ------------------------------- */
  Carousel.prototype.markClone = function (li) {
    li.classList.add('alt-pcard__slide--clone');
    li.setAttribute('aria-hidden', 'true');
    var link = li.querySelector('a');
    if (link) link.setAttribute('tabindex', '-1');
  };

  Carousel.prototype.addClones = function () {
    if (this.cloned || this.realCount < 2) return;
    var kids = this.track.children;
    var head = kids[0].cloneNode(true); // copy of first real → trailing clone
    var tail = kids[this.realCount - 1].cloneNode(true); // copy of last real → leading clone
    this.markClone(head);
    this.markClone(tail);
    this.track.appendChild(head);
    this.track.insertBefore(tail, this.track.children[0]);
    this.cloned = true;
    this.domIndex = 1; // first real now sits at DOM index 1 (after the leading clone)
    var self = this;
    this.jumpTo(1);
    requestAnimationFrame(function () {
      if (self.cloned && self.pendingSnap == null) self.jumpTo(self.domIndex);
    });
  };

  Carousel.prototype.removeClones = function () {
    if (!this.cloned) return;
    var clones = this.track.querySelectorAll('.alt-pcard__slide--clone');
    for (var i = 0; i < clones.length; i++) {
      clones[i].parentNode.removeChild(clones[i]);
    }
    this.cloned = false;
    this.pendingSnap = null;
    this.domIndex = 0;
    this.jumpTo(0);
  };

  Carousel.prototype.setupViewport = function () {
    if (this.realCount < 2) return;
    if (mqDesktop.matches) this.addClones();
    else this.removeClones();
  };

  /* ---- Crossfade (hover reveal + return-to-hero) ------------------------- */
  Carousel.prototype.currentImgSrc = function () {
    var slide = this.track.children[this.domIndex];
    var img = slide && slide.querySelector('img');
    return img ? img.currentSrc || img.src : '';
  };

  Carousel.prototype.killXfade = function () {
    if (this.xfadeTimer) {
      clearTimeout(this.xfadeTimer);
      this.xfadeTimer = 0;
    }
    if (!this.xfade) return;
    this.xfade.style.transition = 'none';
    this.xfade.style.opacity = '0';
  };

  // Crossfade the displayed image to real DOM index `target`: cover with the
  // outgoing image, jump the track underneath (masked), then fade the cover out.
  //
  // ALT-CARD-GLITCH-001 (2026-08-16): rapid enter/leave caused a visible
  // 1st → 2nd → 1st flash. Root cause: if the mouse left before the first
  // crossfade's 340 ms fade-out finished, the second crossfadeTo() snapshot
  // read the *track* position (already at image 2) while the old cover was
  // still mid-fade on top: so the new cover flashed image 2 again before
  // jumping back to image 1. Fix: fully cancel any in-flight fade first
  // (clear the transition and force opacity to its final state on the next
  // frame), and start each crossfade from the cover's CURRENT visible state
  // instead of assuming the track is the source of truth.
  Carousel.prototype.crossfadeTo = function (target) {
    if (!this.cloned || this.realCount < 2) return;
    if (!this.xfade || mqReduce.matches) {
      this.domIndex = target;
      this.jumpTo(target);
      return;
    }
    var x = this.xfade;

    // 1) Cancel any in-flight fade so it can't race this one.
    if (this.xfadeTimer) {
      clearTimeout(this.xfadeTimer);
      this.xfadeTimer = 0;
    }
    x.style.transition = 'none';

    // 2) Snapshot whatever is actually on screen right now. If a previous
    //    cover is still visible (mid-fade), it IS the outgoing image.
    var coverVisible = parseFloat(x.style.opacity || '0') > 0 && x.src;
    var out = coverVisible ? x.src : this.currentImgSrc();
    x.srcset = '';
    if (out) x.src = out;
    x.style.opacity = '1'; // instant cover: identical to what's on screen
    void x.offsetHeight; // commit the cover before moving the track

    this.domIndex = target;
    this.jumpTo(target); // masked by the opaque cover
    void x.offsetHeight;

    x.style.transition = 'opacity ' + XFADE_MS + 'ms var(--alt-motion-ease, ease)';
    x.style.opacity = '0'; // fade the cover out → reveals the new position

    // 3) Once the fade completes, drop the transition so a later hover can
    //    re-cover instantly without fighting a leftover transition.
    var self = this;
    this.xfadeTimer = setTimeout(function () {
      self.xfadeTimer = 0;
      x.style.transition = 'none';
    }, XFADE_MS + 40);
  };

  /* ---- Navigation -------------------------------------------------------- */
  Carousel.prototype.flushSnap = function () {
    if (this.pendingSnap == null) return;
    this.jumpTo(this.pendingSnap);
    this.pendingSnap = null;
  };

  Carousel.prototype.armSnap = function () {
    var self = this;
    if (this.snapTimer) clearTimeout(this.snapTimer);
    this.snapTimer = setTimeout(function () { self.onSettle(); }, 700);
  };

  Carousel.prototype.onSettle = function () {
    if (this.pendingSnap == null) return;
    this.jumpTo(this.pendingSnap);
    this.pendingSnap = null;
  };

  Carousel.prototype.next = function () {
    if (!this.cloned) return;
    this.flushSnap();
    var target = this.domIndex + 1;
    this.animateTo(target);
    if (target === this.realCount + 1) {
      this.pendingSnap = 1; // slid into the trailing clone (looks like slide 1) → snap to real 1
      this.domIndex = 1;
    } else {
      this.domIndex = target;
    }
    this.armSnap();
  };

  Carousel.prototype.prev = function () {
    if (!this.cloned) return;
    this.flushSnap();
    var target = this.domIndex - 1;
    this.animateTo(target);
    if (target === 0) {
      this.pendingSnap = this.realCount; // slid into the leading clone (looks like last) → snap to real last
      this.domIndex = this.realCount;
    } else {
      this.domIndex = target;
    }
    this.armSnap();
  };

  /* ---- Binding ----------------------------------------------------------- */
  Carousel.prototype.bind = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.killXfade();
        self.prev();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.killXfade();
        self.next();
      });
    }

    // Hover: image 2 appears (crossfade). The track really moves to image 2, so
    // the first arrow slide advances to image 3.
    this.root.addEventListener('mouseenter', function () {
      if (!mqDesktop.matches || !self.cloned) return;
      self.killXfade();
      self.crossfadeTo(2);
    });
    // Leave: crossfade back to the hero (image 1).
    this.root.addEventListener('mouseleave', function () {
      if (!mqDesktop.matches || !self.cloned) return;
      self.killXfade();
      self.pendingSnap = null;
      self.crossfadeTo(1);
    });

    this.track.addEventListener('scrollend', function () { self.onSettle(); });

    this.track.addEventListener(
      'scroll',
      function () {
        if (self.ticking) return;
        self.ticking = true;
        requestAnimationFrame(function () {
          self.updateProgress();
          self.ticking = false;
        });
      },
      { passive: true }
    );

    if (mqDesktop.addEventListener) {
      mqDesktop.addEventListener('change', function () {
        self.killXfade();
        self.setupViewport();
        self.updateProgress();
      });
    }

    window.addEventListener('resize', function () {
      if (self.cloned && self.pendingSnap == null) self.jumpTo(self.domIndex);
      self.updateProgress();
    });
  };

  /* ---- Mobile progress line ---------------------------------------------- */
  Carousel.prototype.count = function () {
    return Math.max(1, Math.round(this.track.scrollWidth / this.slideW()));
  };

  Carousel.prototype.updateProgress = function () {
    if (!this.thumb) return;
    var n = this.count();
    var scrollable = this.track.scrollWidth - this.track.clientWidth;
    var frac = scrollable > 0 ? this.track.scrollLeft / scrollable : 0;
    this.thumb.style.width = 100 / n + '%';
    this.thumb.style.transform = 'translateX(' + frac * (n - 1) * 100 + '%)';
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-pcard-carousel]').forEach(function (el) {
      if (!el.__pcard) el.__pcard = new Carousel(el);
    });
  }

  function initAll(scope) {
    init(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init after Section Rendering swaps the grid (filter/sort) or the editor reloads
  document.addEventListener('shopify:section:load', function () { initAll(); });
  document.addEventListener('alt:collection:rendered', function () { initAll(); });

  // Re-init for cards injected asynchronously: e.g. <product-recommendations>
  // ("You may also like") fetches its grid AFTER load, so those carousels miss
  // the initial init. Observe for added pcard carousels and initialise them.
  if (typeof MutationObserver !== 'undefined') {
    var pending = false;
    var mo = new MutationObserver(function (muts) {
      if (pending) return;
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < (added ? added.length : 0); j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches('[data-pcard-carousel]')) || (n.querySelector && n.querySelector('[data-pcard-carousel]'))) {
            pending = true;
            requestAnimationFrame(function () { pending = false; initAll(document); });
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
